import { t } from "@hakili/i18n";
import { GuardedButton, Modal } from "@hakili/ui";
import { Plus } from "lucide-react";
import { type ReactNode, useState } from "react";

/**
 * `round CN-A`, the founder's two decisions of 11 September on Partenaires:
 *
 * 1. **each type of partner has its own tab, as the drawing draws it**
 *    (`hakili-console.jsx`, `sousOnglets`; `WF-CON-35`): Banques,
 *    Concessionnaires, Experts, Assureurs, the drawing's order and words, the
 *    tab in the URL, under the administration alone;
 * 2. **a tab shows its current partners, and the form to add one opens over a
 *    veil from the tab's « Ajouter » control** , one DESIGN for the four
 *    controls (the plus glyph, the drawing's words per tab) in the drawing's
 *    place, at the right of the tab's sub-header line, above the list, outside
 *    any card; filled where the door exists, greyed with its round in the title
 *    where the tab is `CN-B`'s. The modal is `ui`'s, as « Une idée ? » opens;
 *    its look enters by a drawing in words, its pair pending the designer's
 *    stroke.
 */

export const PARTNER_TABS = ["banques", "concessionnaires", "experts", "assureurs"] as const;
export type PartnerTab = (typeof PARTNER_TABS)[number];

/** The tab the URL names, or the drawing's first. */
export const partnerTabOf = (raw: string | null): PartnerTab =>
  (PARTNER_TABS as readonly string[]).includes(raw ?? "") ? (raw as PartnerTab) : PARTNER_TABS[0];

// The tab words are the URL's own values , DATA, keyed as strings (`09-01`).
const LABELS = new Map<PartnerTab, Parameters<typeof t>[0]>([
  ["banques", "console.partenaires.ongletBanques"],
  ["concessionnaires", "console.partenaires.ongletConcessionnaires"],
  ["experts", "console.partenaires.ongletExperts"],
  ["assureurs", "console.partenaires.ongletAssureurs"],
]);

export function PartnerTabs({
  active,
  onSelect,
  tabs = PARTNER_TABS,
  subtitle = t("console.partenaires.sousTitre"),
}: {
  readonly active: PartnerTab;
  readonly onSelect: (tab: PartnerTab) => void;
  /** `CN-B.2`, WF-CON-35's scope rule: the tabs THIS view holds. */
  readonly tabs?: readonly PartnerTab[];
  readonly subtitle?: string;
}): React.JSX.Element {
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t("console.partenaires.titre")}</h1>
      <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>
      <div role="tablist" data-testid="partenaires-onglets" className="mt-4 flex gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={tab === active}
            data-testid={`onglet-${tab}`}
            onClick={() => {
              onSelect(tab);
            }}
            className={
              "rounded-xl border px-4 py-2.5 text-sm font-medium " +
              (tab === active
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
            }
          >
            {t(LABELS.get(tab) ?? "console.partenaires.titre")}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The tab's sub-header line , the drawing's `h3` and its sentence , with the
 * ONE « Ajouter » design at its right: the plus glyph and the tab's own words,
 * live when a door exists, greyed with its round in the title when it does not.
 */
export function PartnerTabHeader({
  title,
  subtitle,
  control,
}: {
  readonly title: string;
  readonly subtitle: string;
  /** Absent when the tab's directory is not built: the header action is not drawn (`D-1019-2`). */
  readonly control?:
    | { readonly label: string; readonly onClick: () => void }
    | { readonly label: string; readonly reason: string };
}): React.JSX.Element {
  return (
    <div data-testid="partenaires-sous-entete" className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
      </div>
      {control === undefined ? null : "onClick" in control ? (
        <button
          type="button"
          data-testid="partenaires-ajouter"
          onClick={control.onClick}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {control.label}
        </button>
      ) : (
        <GuardedButton disabled reason={control.reason} variant="primary">
          <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
          {control.label}
        </GuardedButton>
      )}
    </div>
  );
}

/**
 * The add form, over the veil, from the tab's control. Closed on success by
 * the caller, which re-reads its list; closed by the cross or the veil
 * otherwise. `ui`'s own `Modal`, as « Une idée ? » opens.
 */
export function PartnerAddModal({
  open,
  title,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): React.JSX.Element | null {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      closeLabel={t("console.partenaires.fermer")}
      className="max-w-2xl"
    >
      <div data-testid="partenaires-modal">{children}</div>
    </Modal>
  );
}

/**
 * A tab whose directory is another round's: the drawing's header line, the
 * control greyed with that round in its title, the sentence. Since `CN-B.2`
 * only Assureurs stands here, the insurance round named; Banques is built.
 */
export function PartnerTabInWords({ tab }: { readonly tab: "banques" | "assureurs" }): React.JSX.Element {
  const reason =
    tab === "assureurs" ? t("console.partenaires.assureursRond") : t("console.session.ecranRond");
  return (
    <div data-testid={`partenaires-${tab}`} className="flex flex-col gap-3">
      <PartnerTabHeader
        title={
          tab === "banques" ? t("console.partenaires.banquesTitre") : t("console.partenaires.assureursTitre")
        }
        subtitle={
          tab === "banques" ? t("console.partenaires.banquesSous") : t("console.partenaires.assureursSous")
        }
      />
      <section className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
        <p className="text-sm text-gray-500">{reason}</p>
      </section>
    </div>
  );
}

/** The one gesture the four tabs share: open, then close on success. */
export function useAddPartner(): {
  readonly open: boolean;
  readonly show: () => void;
  readonly hide: () => void;
} {
  const [open, setOpen] = useState(false);
  return {
    open,
    show: () => {
      setOpen(true);
    },
    hide: () => {
      setOpen(false);
    },
  };
}
